import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { IDropdown, IRadio } from 'codex-lib';
import { FormCustomField } from '../model/form-custom-field';
import { CustomFieldTypeEnum } from 'src/app/enum/custom-field-type.enum';

interface FontSizeOption {
  id: number;
  value: string;
}

@Component({
  selector: 'app-envelope-sidebar-edit-custom-field',
  templateUrl: './envelope-sidebar-edit-custom-field.component.html',
  styleUrls: ['./envelope-sidebar-edit-custom-field.component.scss'],
})
export class EnvelopeSidebarEditCustomFieldComponent implements OnInit, OnChanges {
  private readonly pixelToMillimeterFactor = 3.78;
  private readonly defaultFontSize = 12;
  private readonly defaultNumberOfLines = 1;
  private readonly maxNumberOfLines = 10;
  private readonly viewerTextLineHeightMultiplier = 1.2;
  private readonly viewerTextVerticalReservedSpace = 10;

  @Input() customField: FormCustomField;

  @Output() closeModal = new EventEmitter<void>();
  @Output() confirmModal = new EventEmitter<FormCustomField>();
  @Output() deleteModal = new EventEmitter<void>();

  readonly customFieldTypeEnum = CustomFieldTypeEnum;
  readonly fontSizeOptions: FontSizeOption[] = [
    { id: 8, value: '8' },
    { id: 9, value: '9' },
    { id: 10, value: '10' },
    { id: 11, value: '11' },
    { id: 12, value: '12' },
    { id: 14, value: '14' },
    { id: 16, value: '16' },
    { id: 18, value: '18' },
  ];
  readonly componentForm: FormGroup;
  readonly fontSizedropdown: IDropdown = {
    items: this.fontSizeOptions,
    displayText: 'value',
    placeholder: '--',
    showSearch: false,
  };

  fontSizeSelected: FontSizeOption;
  requiredYes: IRadio;
  requiredNo: IRadio;
  checkedRequiredState = true;

  constructor() {
    this.componentForm = new FormGroup({
      displayName: new FormControl(''),
      numberOfLines: new FormControl(this.defaultNumberOfLines),
      fontSize: new FormControl(null),
      required: new FormControl(true),
    });
  }

  get displayName(): FormControl {
    return this.componentForm.get('displayName') as FormControl;
  }

  get fontSize(): FormControl {
    return this.componentForm.get('fontSize') as FormControl;
  }

  get numberOfLines(): FormControl {
    return this.componentForm.get('numberOfLines') as FormControl;
  }

  get required(): FormControl {
    return this.componentForm.get('required') as FormControl;
  }

  ngOnInit(): void {
    this.requiredYes = {
      span: 'yes',
    };

    this.requiredNo = {
      span: 'no',
    };
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['customField']) {
      this.setValueCustomField();
    }
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onDelete(): void {
    this.deleteModal.emit();
  }

  onConfirm(): void {
    if (!this.customField) {
      return;
    }

    const selectedFontSize = this.fontSize.value;
    const fontSizeValue =
    typeof selectedFontSize === 'object'
        ? Number((selectedFontSize as FontSizeOption).id)
        : Number(selectedFontSize);
    const fontToSize = Number.isFinite(fontSizeValue) ? fontSizeValue : this.defaultFontSize;
    const numberOfLines = this.normalizeNumberOfLines(this.numberOfLines.value);
    const checkboxFieldSizePx = fontToSize + 8;
    
    const fieldToSave = Object.assign(new FormCustomField(), this.customField);
    fieldToSave.displayName = (this.displayName.value || '').toString().trim();
    fieldToSave.required = !!this.required.value;
    fieldToSave.fontSize = fontToSize;
    
    if (fieldToSave.fieldType === CustomFieldTypeEnum.CheckboxField) {
      this.applyCheckboxFieldSize(fieldToSave, checkboxFieldSizePx);
    } else {
      this.applyTextFieldSize(fieldToSave, numberOfLines);
    }

    this.confirmModal.emit(fieldToSave);
  }

  onFontSizeSelectItem(value: FontSizeOption): void {
    this.fontSizeSelected = value;
    this.fontSize.setValue(value);
  }

  changeYes(value: boolean): void {
    if (value === true) {
      this.checkedRequiredState = true;
      this.required.setValue(true);
    }
  }

  changeNo(value: boolean): void {
    if (value === true) {
      this.checkedRequiredState = false;
      this.required.setValue(false);
    }
  }

  private setValueCustomField(): void {
    if (!this.customField) {
      return;
    }

    this.displayName.setValue(this.customField.displayName || '');
    this.checkedRequiredState = this.customField.required !== false;
    this.required.setValue(this.checkedRequiredState);

    const selected = this.fontSizeOptions.find(
      (item) => item.id === Number(this.customField.fontSize)
    );

    this.fontSizeSelected = selected || this.fontSizeOptions.find((item) => item.id === 12);
    this.fontSize.setValue(this.fontSizeSelected);

    const storedNumberOfLines = Number(this.customField.numberOfLines);
    if (Number.isFinite(storedNumberOfLines)) {
      this.numberOfLines.setValue(this.normalizeNumberOfLines(storedNumberOfLines));
      return;
    }

    const currentFontSize = Number(this.customField.fontSize) || this.defaultFontSize;
    const currentPixelHeight = Number(this.customField.sizePixelHeight);
    if (Number.isFinite(currentPixelHeight) && currentPixelHeight > 0) {
      const inferredFromPixel = Math.round(
        (currentPixelHeight - this.viewerTextVerticalReservedSpace) /
        (currentFontSize * this.viewerTextLineHeightMultiplier)
      );
      this.numberOfLines.setValue(this.normalizeNumberOfLines(inferredFromPixel));
      return;
    }

    const currentSizeHeight = Number(this.customField.sizeHeight);
    const pixelHeight = Number.isFinite(currentSizeHeight)
      ? currentSizeHeight * this.pixelToMillimeterFactor
      : 25;
    const inferredNumberOfLines = Math.round(
      (pixelHeight - this.viewerTextVerticalReservedSpace) /
      (currentFontSize * this.viewerTextLineHeightMultiplier)
    );
    this.numberOfLines.setValue(this.normalizeNumberOfLines(inferredNumberOfLines));
  }

  private normalizeNumberOfLines(value: unknown): number {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return this.defaultNumberOfLines;
    }

    return Math.min(this.maxNumberOfLines, Math.max(this.defaultNumberOfLines, Math.floor(parsed)));
  }

  private applyTextFieldSize(fieldToSave: FormCustomField, numberOfLines: number): void {
      fieldToSave.numberOfLines = numberOfLines;
      const pixelHeight = Math.ceil(
        (fieldToSave.fontSize * this.viewerTextLineHeightMultiplier * numberOfLines) +
        this.viewerTextVerticalReservedSpace
      );
      fieldToSave.sizePixelHeight = pixelHeight;
      fieldToSave.sizeHeight =
      Math.round((pixelHeight / this.pixelToMillimeterFactor) * 100) / 100;
  }

  private applyCheckboxFieldSize(fieldToSave: FormCustomField, checkboxFieldSizePx: number): void {
      fieldToSave.sizePixelHeight = checkboxFieldSizePx;
      fieldToSave.sizePixelWidth = checkboxFieldSizePx;
      fieldToSave.sizeHeight = checkboxFieldSizePx / this.pixelToMillimeterFactor;
      fieldToSave.sizeWidth = checkboxFieldSizePx / this.pixelToMillimeterFactor;
  }
}
